/**
 * Invitations addressed to the current user, plus accept. Drives onboarding for
 * org-only accounts that have not joined an org yet.
 *
 * Accept is a small chain: accept the invitation → set the joined org active
 * (so the user lands in a usable context) → `refresh()` the JWT → invalidate
 * `me` + `organizations` so the app re-renders in the new org.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { refresh } from '../lib/endpoints/auth'
import {
  acceptInvitation,
  listMyInvitations,
  rejectInvitation,
  setActiveOrganization,
} from '../lib/endpoints/organizations'
import { qk } from '../lib/queryKeys'

export function useMyInvitations() {
  const qc = useQueryClient()

  const query = useQuery({
    queryKey: qk.myInvitations(),
    queryFn: listMyInvitations,
  })

  const accept = useMutation<
    void,
    Error,
    { invitationId: string; organizationId: string }
  >({
    mutationFn: async ({ invitationId, organizationId }) => {
      await acceptInvitation(invitationId)
      // Land the user in the org they just joined.
      await setActiveOrganization(organizationId)
      await refresh()
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.me() })
      void qc.invalidateQueries({ queryKey: qk.organizations() })
      void qc.invalidateQueries({ queryKey: qk.myInvitations() })
    },
  })

  // Decline — no context change, just drop it from the pending list.
  const reject = useMutation<void, Error, string>({
    mutationFn: (invitationId: string) => rejectInvitation(invitationId),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.myInvitations() }),
  })

  return { query, accept, reject }
}
